import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CalendarIcon, 
  TagIcon, 
  EyeIcon,
  PlayIcon,
  LinkIcon,
  PhotoIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  PaperClipIcon
} from '@heroicons/react/24/outline';

interface ResearchItem {
  _id: string;
  title: string;
  description: string;
  year: number;
  category: string;
  journal: string;
  impact: string;
  content: {
    type: 'text' | 'image' | 'video' | 'link' | 'mixed';
    text?: string;
    images?: Array<{
      url: string;
      caption: string;
      alt: string;
    }>;
    video?: {
      url: string;
      title: string;
      description: string;
    };
    link?: {
      url: string;
      title: string;
      description: string;
    };
    mixed?: {
      text: string;
      images: Array<{
        url: string;
        caption: string;
        alt: string;
      }>;
      videos: Array<{
        url: string;
        title: string;
        description: string;
      }>;
      links: Array<{
        url: string;
        title: string;
        description: string;
      }>;
    };
  };
  authors: string[];
  tags: string[];
  viewCount: number;
  externalLinks?: Array<{
    title: string;
    url: string;
    description: string;
  }>;
  attachments?: Array<{
    name: string;
    url: string;
    size: number;
    type: string;
  }>;
  createdAt: string;
}

const ResearchDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [research, setResearch] = useState<ResearchItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchResearchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/research/${id}`);
      const result = await response.json();
      
      if (result.success) {
        setResearch(result.data);
      } else {
        setError(result.message || '获取研究成果详情失败');
      }
    } catch (error) {
      console.error('获取研究成果详情失败:', error);
      setError('网络错误，请稍后重试');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      fetchResearchDetail();
    }
  }, [id, fetchResearchDetail]);

  const renderContent = (item: ResearchItem) => {
    const { content } = item;
    
    switch (content.type) {
      case 'text':
        return (
          <div 
            className="prose max-w-none text-gray-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: content.text || '' }}
          />
        );
      
      case 'image':
        return (
          <div className="space-y-6">
            {content.images?.map((image, index) => (
              <div key={index} className="text-center">
                <img 
                  src={image.url} 
                  alt={image.alt || image.caption}
                  className="max-w-full h-auto rounded-lg shadow-lg mx-auto"
                />
                {image.caption && (
                  <p className="text-sm text-gray-600 mt-3 text-center">{image.caption}</p>
                )}
              </div>
            ))}
          </div>
        );
      
      case 'video':
        return (
          <div className="space-y-6">
            <div className="aspect-video">
              <iframe
                src={content.video?.url}
                title={content.video?.title}
                className="w-full h-full rounded-lg shadow-lg"
                frameBorder="0"
                allowFullScreen
              />
            </div>
            {content.video?.description && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2">{content.video.title}</h4>
                <p className="text-gray-700">{content.video.description}</p>
              </div>
            )}
          </div>
        );
      
      case 'link':
        return (
          <div className="space-y-6">
            <div className="bg-blue-50 p-6 rounded-lg">
              <a 
                href={content.link?.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-800 text-lg font-medium"
              >
                <LinkIcon className="w-5 h-5 mr-2" />
                {content.link?.title}
              </a>
              {content.link?.description && (
                <p className="text-gray-700 mt-3">{content.link.description}</p>
              )}
            </div>
          </div>
        );
      
      case 'mixed':
        return (
          <div className="space-y-8">
            {content.mixed?.text && (
              <div 
                className="prose max-w-none text-gray-700 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content.mixed.text }}
              />
            )}
            
            {content.mixed?.images && content.mixed.images.length > 0 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold flex items-center text-gray-800">
                  <PhotoIcon className="w-6 h-6 mr-2" />
                  相关图片
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {content.mixed.images.map((image, index) => (
                    <div key={index} className="text-center">
                      <img 
                        src={image.url} 
                        alt={image.alt || image.caption}
                        className="max-w-full h-auto rounded-lg shadow-md"
                      />
                      {image.caption && (
                        <p className="text-sm text-gray-600 mt-2">{image.caption}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {content.mixed?.videos && content.mixed.videos.length > 0 && (
              <div className="space-y-6">
                <h3 className="text-xl font-semibold flex items-center text-gray-800">
                  <PlayIcon className="w-6 h-6 mr-2" />
                  相关视频
                </h3>
                <div className="space-y-6">
                  {content.mixed.videos.map((video, index) => (
                    <div key={index}>
                      <div className="aspect-video">
                        <iframe
                          src={video.url}
                          title={video.title}
                          className="w-full h-full rounded-lg shadow-lg"
                          frameBorder="0"
                          allowFullScreen
                        />
                      </div>
                      {video.description && (
                        <div className="bg-gray-50 p-4 rounded-lg mt-3">
                          <h4 className="font-semibold mb-2">{video.title}</h4>
                          <p className="text-gray-700">{video.description}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {content.mixed?.links && content.mixed.links.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-xl font-semibold flex items-center text-gray-800">
                  <LinkIcon className="w-6 h-6 mr-2" />
                  相关链接
                </h3>
                <div className="space-y-3">
                  {content.mixed.links.map((link, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg">
                      <a 
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block text-blue-600 hover:text-blue-800 font-medium"
                      >
                        {link.title}
                      </a>
                      {link.description && (
                        <p className="text-gray-700 mt-1 text-sm">{link.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-gray-600">加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !research) {
    return (
      <div className="max-w-4xl mx-auto py-8">
        <div className="text-center">
          <DocumentTextIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">研究成果不存在</h2>
          <p className="text-gray-600 mb-6">{error || '该研究成果可能已被删除或移动'}</p>
          <button
            onClick={() => navigate('/research-achievements')}
            className="btn btn-primary"
          >
            返回研究成果列表
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto py-8">
      {/* 返回按钮 */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/research-achievements')}
          className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          返回研究成果列表
        </button>
      </div>

      {/* 文章头部 */}
      <div className="mb-8">
        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
          <span className="flex items-center">
            <CalendarIcon className="w-4 h-4 mr-1" />
            {research.year}年
          </span>
          <span className="flex items-center">
            <TagIcon className="w-4 h-4 mr-1" />
            {research.category}
          </span>
          <span className="flex items-center">
            <EyeIcon className="w-4 h-4 mr-1" />
            {research.viewCount} 次浏览
          </span>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">{research.title}</h1>
        
        {/* 期刊/会议名称标签 */}
        {research.journal && (
          <div className="mb-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
              📖 {research.journal}
            </span>
            {research.impact && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200 ml-2">
                📊 {research.impact}
              </span>
            )}
          </div>
        )}
        
        <p className="text-lg text-gray-600 mb-6">{research.description}</p>

        {/* 作者信息 */}
        {research.authors && research.authors.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">作者</h3>
            <div className="flex flex-wrap gap-3">
              {research.authors.map((author, index) => (
                <div key={index} className="bg-gray-100 px-3 py-2 rounded-lg">
                  <span className="text-sm font-medium">{author}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* 标签 */}
        {research.tags && research.tags.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">标签</h3>
            <div className="flex flex-wrap gap-2">
              {research.tags.map((tag, index) => (
                <span key={index} className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 文章内容 */}
      <div className="bg-white rounded-lg shadow-sm border p-8 mb-8">
        {renderContent(research)}
      </div>

      {/* 外部链接 */}
      {research.externalLinks && research.externalLinks.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">外部链接</h3>
          <div className="space-y-3">
            {research.externalLinks.map((link, index) => (
              <div key={index} className="flex items-center space-x-3">
                <LinkIcon className="w-4 h-4 text-gray-500" />
                <a
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 font-medium"
                >
                  {link.title}
                </a>
                {link.description && (
                  <span className="text-gray-600 text-sm">- {link.description}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 附件 */}
      {research.attachments && research.attachments.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
            <PaperClipIcon className="w-5 h-5 mr-2" />
            附件
          </h3>
          <div className="space-y-3">
            {research.attachments.map((attachment, index) => (
              <div key={index} className="flex items-center justify-between bg-white p-3 rounded-lg border">
                <div className="flex items-center space-x-3">
                  <PaperClipIcon className="w-4 h-4 text-gray-500" />
                  <span className="font-medium">{attachment.name}</span>
                  <span className="text-gray-500 text-sm">({attachment.type})</span>
                </div>
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline"
                >
                  下载
                </a>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ResearchDetail; 